import { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Upload, FileText, X, Loader2, Check, AlertCircle } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

interface BulkResumeUploadProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  jobId?: string;
  onSuccess?: () => void;
}

interface UploadedFile {
  file: File;
  status: 'pending' | 'uploading' | 'success' | 'error';
  error?: string;
  candidateId?: string;
}

const BulkResumeUpload = ({ open, onOpenChange, jobId, onSuccess }: BulkResumeUploadProps) => {
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [selectedJobId, setSelectedJobId] = useState(jobId || '');
  const [jobs, setJobs] = useState<any[]>([]);
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();
  const { user } = useAuth();

  const fetchJobs = async () => {
    setLoadingJobs(true);
    try {
      const { data, error } = await supabase
        .from('jobs')
        .select('id, title, clients(name)')
        .in('status', ['draft', 'published'])
        .order('created_at', { ascending: false });
      
      if (error) {
        console.error('Error fetching jobs:', error);
      }
      setJobs(data || []);
    } catch (err) {
      console.error('Failed to fetch jobs:', err);
    } finally {
      setLoadingJobs(false);
    }
  };

  // Fetch jobs when dialog opens
  useEffect(() => {
    if (open) {
      fetchJobs();
      if (jobId) setSelectedJobId(jobId);
    } else {
      setFiles([]);
      setProgress(0);
    }
  }, [open, jobId]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || []);
    const validFiles = selectedFiles.filter(f => 
      f.type === 'application/pdf' || 
      f.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
      f.type === 'application/msword'
    );
    
    if (validFiles.length < selectedFiles.length) {
      toast({
        title: 'Some files skipped',
        description: 'Only PDF and DOCX files are supported',
        variant: 'destructive',
      });
    }
    
    setFiles(prev => [...prev, ...validFiles.map(f => ({ file: f, status: 'pending' as const }))]);
  };

  const removeFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const uploadFiles = async () => {
    if (!selectedJobId) {
      toast({ title: 'Please select a job', variant: 'destructive' });
      return;
    }
    
    if (files.length === 0) {
      toast({ title: 'Please select files to upload', variant: 'destructive' });
      return;
    }

    setUploading(true);
    let completed = 0;

    // Get current user's app_users id
    const { data: appUser } = await supabase
      .from('app_users')
      .select('id')
      .eq('user_id', user?.id)
      .single();

    for (let i = 0; i < files.length; i++) {
      const uploadFile = files[i];
      setFiles(prev => prev.map((f, idx) => 
        idx === i ? { ...f, status: 'uploading' } : f
      ));

      try {
        // 1. Create candidate record
        const fileName = uploadFile.file.name;
        const nameParts = fileName.replace(/\.(pdf|docx?)$/i, '').split(/[_\-\s]+/);
        const firstName = nameParts[0] || 'Unknown';
        const lastName = nameParts.slice(1).join(' ') || '';

        const { data: candidate, error: candidateError } = await supabase
          .from('candidates')
          .insert({
            first_name: firstName,
            last_name: lastName || 'Candidate',
          })
          .select()
          .single();

        if (candidateError) throw candidateError;

        // 2. Upload file to storage
        const storagePath = `${candidate.id}/${Date.now()}_${fileName}`;
        const { error: uploadError } = await supabase.storage
          .from('resumes')
          .upload(storagePath, uploadFile.file);

        if (uploadError) throw uploadError;

        // 3. Create resume record
        const { data: resumeRecord, error: resumeError } = await supabase
          .from('candidate_resumes')
          .insert({
            candidate_id: candidate.id,
            original_filename: fileName,
            storage_path: storagePath,
            file_size: uploadFile.file.size,
          })
          .select()
          .single();

        if (resumeError) throw resumeError;

        // 4. Create candidate application linking to job
        const { error: applicationError } = await supabase
          .from('candidate_applications')
          .insert({
            candidate_id: candidate.id,
            job_id: selectedJobId,
            status: 'applied',
          });

        if (applicationError) throw applicationError;

        // 5. Trigger deep parsing with job_id and uploaded_by (fire-and-forget, don't await)
        supabase.functions.invoke('trigger-deep-parse', {
          body: {
            candidateId: candidate.id,
            resumeId: resumeRecord.id,
            fileName: fileName,
            jobId: selectedJobId,
            uploadedBy: appUser?.id,
          },
        }).catch(err => console.error('Failed to trigger parsing:', err));

        setFiles(prev => prev.map((f, idx) => 
          idx === i ? { ...f, status: 'success', candidateId: candidate.id } : f
        ));

      } catch (error: any) {
        console.error('Error uploading file:', error);
        setFiles(prev => prev.map((f, idx) => 
          idx === i ? { ...f, status: 'error', error: error.message } : f
        ));
      }

      completed++;
      setProgress((completed / files.length) * 100);
    }

    setUploading(false);
    const successCount = files.filter(f => f.status === 'success').length;
    toast({
      title: 'Upload complete',
      description: `${successCount} of ${files.length} resumes queued for parsing. You can close this dialog - parsing continues in the background.`,
    });
    
    onSuccess?.();
  };

  const pendingCount = files.filter(f => f.status === 'pending').length;
  const successCount = files.filter(f => f.status === 'success').length;
  const errorCount = files.filter(f => f.status === 'error').length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-visible">
        <DialogHeader>
          <DialogTitle>Bulk Resume Upload</DialogTitle>
          <DialogDescription>
            Upload multiple resumes to automatically create candidate profiles and link them to a job. 
            Parsing happens in the background - you don't need to wait on this screen.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 overflow-y-auto max-h-[60vh] pr-2">
          {/* Job Selection */}
          {!jobId && (
            <div>
              <label className="text-sm font-medium mb-2 block">Select Job</label>
              <Select value={selectedJobId} onValueChange={setSelectedJobId} disabled={loadingJobs}>
                <SelectTrigger className="w-full">
                  {loadingJobs ? (
                    <span className="flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Loading jobs...
                    </span>
                  ) : (
                    <SelectValue placeholder="Choose a job..." />
                  )}
                </SelectTrigger>
                <SelectContent 
                  className="z-[200] bg-background border shadow-lg"
                  position="popper"
                  sideOffset={4}
                >
                  {loadingJobs ? (
                    <div className="p-4 text-center text-muted-foreground text-sm flex items-center justify-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Loading...
                    </div>
                  ) : jobs.length === 0 ? (
                    <div className="p-4 text-center text-muted-foreground text-sm">
                      No jobs available. Create a job first.
                    </div>
                  ) : (
                    jobs.map(job => (
                      <SelectItem key={job.id} value={job.id}>
                        {job.title} {job.clients?.name && `- ${job.clients.name}`}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Drop Zone */}
          <div
            className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer hover:border-primary/50 transition-colors"
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload className="h-10 w-10 mx-auto mb-3 text-muted-foreground" />
            <p className="text-sm font-medium">Click to upload or drag and drop</p>
            <p className="text-xs text-muted-foreground mt-1">PDF or DOCX files only</p>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept=".pdf,.doc,.docx"
              className="hidden"
              onChange={handleFileSelect}
            />
          </div>

          {/* File List */}
          {files.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span>{files.length} file(s) selected</span>
                {successCount > 0 && (
                  <span className="text-green-600">{successCount} uploaded</span>
                )}
                {errorCount > 0 && (
                  <span className="text-destructive">{errorCount} failed</span>
                )}
              </div>
              
              <div className="max-h-48 overflow-y-auto space-y-2">
                {files.map((f, i) => (
                  <div key={i} className="flex items-center gap-3 p-2 bg-muted/50 rounded">
                    <FileText className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                    <span className="text-sm flex-1 truncate">{f.file.name}</span>
                    <span className="text-xs text-muted-foreground">
                      {(f.file.size / 1024).toFixed(0)} KB
                    </span>
                    {f.status === 'pending' && !uploading && (
                      <Button variant="ghost" size="sm" onClick={() => removeFile(i)}>
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                    {f.status === 'uploading' && (
                      <Loader2 className="h-4 w-4 animate-spin text-primary" />
                    )}
                    {f.status === 'success' && (
                      <Check className="h-4 w-4 text-green-600" />
                    )}
                    {f.status === 'error' && (
                      <span title={f.error}>
                        <AlertCircle className="h-4 w-4 text-destructive" />
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Progress */}
          {uploading && (
            <div className="space-y-2">
              <Progress value={progress} />
              <p className="text-sm text-center text-muted-foreground">
                Uploading and queuing for parsing... {Math.round(progress)}%
              </p>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 justify-end">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={uploading}>
              {uploading ? 'Please wait...' : successCount === files.length && files.length > 0 ? 'Close' : 'Cancel'}
            </Button>
            {successCount === files.length && files.length > 0 ? (
              <Button onClick={() => onOpenChange(false)}>
                <Check className="h-4 w-4 mr-2" />
                Done - View Parsed Candidates
              </Button>
            ) : (
              <Button onClick={uploadFiles} disabled={uploading || pendingCount === 0 || !selectedJobId}>
                {uploading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4 mr-2" />
                    Upload {pendingCount} Resume(s)
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BulkResumeUpload;
